/**
 * The ONE subject-only rule for a generated sprite's art line. A kind's art
 * line (a template's archetype `role`, a synthesis patch's role rewrite, the
 * recommender's per-slot line) is the SUBJECT of one isolated sprite: the
 * object it draws, on its own. Its situation in play — what holds, carries or
 * captures it, what it rides, where it sits relative to another object — is
 * gameplay the game itself stages by placing, tethering and moving separate
 * sprites (2026-10-19 Stolen Wings: the captive fighter's line "held captive
 * beneath a guardian" came from the template role "carried off by a guardian";
 * the image judge then demanded the guardian on two attempts and forbade it on
 * the third, and the mandatory base sprite failed the build).
 *
 * The situations are DATA, read by every party that names them, never a copy:
 * the recommender and spec-synthesis prompts that author art lines, the
 * sprite judge's `rightSubject` criterion, and {@link subjectSituationsNamedV1},
 * the deterministic check the template catalogue, `deriveComposedGameV1` and
 * the recommender's reply parser apply — so the line the model writes, the
 * check that admits it and the verdict that accepts it name the same
 * situations.
 */

/** Each situation as the prose names it and the phrasings that state it. */
const SITUATIONS: ReadonlyArray<{ name: string; forms: string }> = [
  { name: 'captive or captured', forms: 'captives?|captured|imprisoned|abducted|kidnapped' },
  {
    name: 'held, carried or towed by something',
    forms: '(?:held|carried|towed|dragged|hauled|clutched|gripped|grabbed)\\s+(?:(?:off|away|up|aloft)\\s+)?(?:by|beneath|below|under|in)',
  },
  { name: 'riding or mounted on something', forms: 'riding|ridden|mounted\\s+on|perched\\s+on' },
  {
    name: 'placed beneath, above, beside, toward or onto another object',
    forms: '(?:beneath|below|underneath|above|atop|beside|alongside|toward|towards|onto|next\\s+to|on\\s+top\\s+of|in\\s+front\\s+of'
      + '|attached\\s+to|tethered\\s+to|docked\\s+(?:to|with|beside))\\s+(?:a|an|the)',
  },
];

/** The situations as one prose list, for prompts and the judge rubric. */
export const SUBJECT_SITUATIONS = `${SITUATIONS.slice(0, -1).map(s => s.name).join(', ')} or ${SITUATIONS.at(-1)!.name}`;

const MATCHERS = SITUATIONS.map(s => ({ name: s.name, re: new RegExp(`\\b(?:${s.forms})\\b`, 'i') }));

/** The situations a sprite art line states (prose names, list order); empty
 * when the line describes its subject alone. */
export function subjectSituationsNamedV1(line: string): string[] {
  return MATCHERS.filter(m => m.re.test(line)).map(m => m.name);
}
